/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDeviceIntegrationConfigRequest,
  GetDeviceIntegrationConfigSecurity,
} from "../models/operations/getdeviceintegrationconfig.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDeviceIntegrationConfigQuery,
  DeviceIntegrationConfigQueryData,
  prefetchDeviceIntegrationConfig,
  queryKeyDeviceIntegrationConfig,
} from "./deviceIntegrationConfig.core.js";
export {
  buildDeviceIntegrationConfigQuery,
  type DeviceIntegrationConfigQueryData,
  prefetchDeviceIntegrationConfig,
  queryKeyDeviceIntegrationConfig,
};

export type DeviceIntegrationConfigQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getConfig deviceIntegrations
 *
 * @remarks
 * Get the org-wide device integration config for a provider. Returns an empty config (enabled=false, has_credentials=false) when none is set.
 */
export function useDeviceIntegrationConfig(
  request: GetDeviceIntegrationConfigRequest,
  security?: GetDeviceIntegrationConfigSecurity | undefined,
  options?: QueryHookOptions<
    DeviceIntegrationConfigQueryData,
    DeviceIntegrationConfigQueryError
  >,
): UseQueryResult<
  DeviceIntegrationConfigQueryData,
  DeviceIntegrationConfigQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDeviceIntegrationConfigQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getConfig deviceIntegrations
 *
 * @remarks
 * Get the org-wide device integration config for a provider. Returns an empty config (enabled=false, has_credentials=false) when none is set.
 */
export function useDeviceIntegrationConfigSuspense(
  request: GetDeviceIntegrationConfigRequest,
  security?: GetDeviceIntegrationConfigSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DeviceIntegrationConfigQueryData,
    DeviceIntegrationConfigQueryError
  >,
): UseSuspenseQueryResult<
  DeviceIntegrationConfigQueryData,
  DeviceIntegrationConfigQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDeviceIntegrationConfigQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDeviceIntegrationConfigData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DeviceIntegrationConfigQueryData,
): DeviceIntegrationConfigQueryData | undefined {
  const key = queryKeyDeviceIntegrationConfig(...queryKeyBase);

  return client.setQueryData<DeviceIntegrationConfigQueryData>(key, data);
}

export function invalidateDeviceIntegrationConfig(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "deviceIntegrations",
      "getConfig",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDeviceIntegrationConfig(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deviceIntegrations", "getConfig"],
  });
}
